import { ThemeEvents } from '@theme/events';

const RETRY_DELAY_MS = 300;
const MAX_RETRIES = 20;

/**
 * @typedef {{ rate?: string | number }} Studio19Rate
 * @typedef {{ min_rate?: string | number, rates?: Studio19Rate[] }} Studio19VariantData
 */

/** @param {string | number | null | undefined} value */
function parseRate(value) {
  const numeric = Number.parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

/** @param {Studio19VariantData | null | undefined} variantData */
function getRateFromVariantData(variantData) {
  if (!variantData || typeof variantData !== 'object') return null;

  const minRate = parseRate(variantData.min_rate);
  if (minRate !== null) return minRate;

  if (!Array.isArray(variantData.rates)) return null;

  /** @type {number | null} */
  let minFromRates = null;
  for (const rateEntry of variantData.rates) {
    const parsedRate = parseRate(rateEntry?.rate);
    if (parsedRate === null) continue;
    if (minFromRates === null || parsedRate < minFromRates) {
      minFromRates = parsedRate;
    }
  }

  return minFromRates;
}

/** @param {number} rate */
function formatRate(rate) {
  if (!Number.isFinite(rate)) return null;

  const rounded = Math.round(rate * 100) / 100;
  if (Number.isInteger(rounded)) return `$${rounded}`;

  return `$${rounded.toFixed(2)}`;
}

function getCurrentVariantId() {
  const urlVariant = new URL(window.location.href).searchParams.get('variant');
  if (urlVariant) return urlVariant;

  const formVariantInput = document.querySelector('.product-details product-form-component input[name="id"]');
  if (formVariantInput instanceof HTMLInputElement && formVariantInput.value) {
    return formVariantInput.value;
  }

  const selectedOption = document.querySelector('variant-picker [data-variant-id][selected], variant-picker [data-variant-id]:checked');
  if (selectedOption instanceof HTMLElement && selectedOption.dataset.variantId) {
    return selectedOption.dataset.variantId;
  }

  return null;
}

function getTopStudio19TextNodes() {
  // Preferred: a stable, explicit hook rendered by blocks/pdp-payment-options.liquid.
  // Decoupled from the Studio19 logo filename so the payment card can restyle freely.
  const explicitNodes = Array.from(document.querySelectorAll('.product-details p[data-s19-rent]')).filter(
    (paragraph) => paragraph instanceof HTMLParagraphElement,
  );
  if (explicitNodes.length) return explicitNodes;

  // Legacy fallback: locate the paragraph next to the Studio19 logo image
  // (used by the standalone Studio19 landing page / older layouts).
  return Array.from(document.querySelectorAll('.product-details img[src*="Studio19_Logo"]'))
    .map((logo) => {
      const iconContainer = logo.closest('span, div');
      const textContainer = iconContainer?.nextElementSibling;
      if (!textContainer) return null;

      const paragraph = textContainer.querySelector('p');
      return paragraph instanceof HTMLParagraphElement ? paragraph : null;
    })
    .filter((paragraph) => paragraph !== null);
}

function getTopStudio19ApplyNodes() {
  const existingApplyLinks = Array.from(document.querySelectorAll('.studio19-top-apply')).filter(
    (link) => link instanceof HTMLAnchorElement,
  );
  if (existingApplyLinks.length) return existingApplyLinks;

  return getTopStudio19TextNodes()
    .map((paragraph) => {
      if (!paragraph.isConnected) return null;

      const applyLink = document.createElement('a');
      applyLink.className = 'studio19-top-apply is-hidden is-disabled';
      applyLink.textContent = 'Apply Now';
      applyLink.setAttribute('aria-disabled', 'true');
      applyLink.setAttribute('tabindex', '-1');
      // Sibling (not child) of the rent paragraph so it can bottom-align independently of the text.
      paragraph.after(applyLink);

      return applyLink;
    })
    .filter((link) => link instanceof HTMLAnchorElement);
}

function getVendorApplyHref() {
  const vendorApplyButton = document.querySelector('.studio19-wrapper .s19-component .s19-apply-btn[href]');
  if (!(vendorApplyButton instanceof HTMLAnchorElement)) return null;

  const href = vendorApplyButton.getAttribute('href');
  return href && href.trim() ? href : null;
}

/**
 * True when the Studio19 rate data has loaded but the current variant has no rental rate
 * (no entry, or an entry with no usable rate) — i.e. Studio19 is genuinely unavailable for it,
 * as opposed to the data simply not having loaded yet.
 */
function isStudio19RateUnavailable() {
  const variantId = getCurrentVariantId();
  if (!variantId) return false;

  /** @type {Record<string, Studio19VariantData> | undefined} */
  const allVariantData = /** @type {{ initialVariantData?: Record<string, Studio19VariantData> }} */ (window)
    .initialVariantData;
  if (!allVariantData || typeof allVariantData !== 'object') return false;

  return getRateFromVariantData(allVariantData[String(variantId)]) === null;
}

/**
 * Studio19 has no rental for this product/variant: show an explicit "Unavailable" label
 * (instead of a bare "Rent from") and hide the Apply link.
 */
function setTopStudio19Unavailable() {
  const textNodes = getTopStudio19TextNodes();
  textNodes.forEach((paragraph) => {
    paragraph.textContent = 'Unavailable';
  });
  setTopStudio19ApplyEmpty();
}

function setTopStudio19ApplyEmpty() {
  const applyNodes = getTopStudio19ApplyNodes();

  applyNodes.forEach((link) => {
    link.removeAttribute('href');
    link.setAttribute('aria-disabled', 'true');
    link.setAttribute('tabindex', '-1');
    link.classList.add('is-hidden', 'is-disabled');
  });
}

function updateTopStudio19ApplyLink() {
  const applyNodes = getTopStudio19ApplyNodes();
  if (!applyNodes.length) return false;

  const vendorHref = getVendorApplyHref();
  if (!vendorHref) {
    setTopStudio19ApplyEmpty();
    return false;
  }

  applyNodes.forEach((link) => {
    link.setAttribute('href', vendorHref);
    link.removeAttribute('aria-disabled');
    link.removeAttribute('tabindex');
    link.classList.remove('is-hidden', 'is-disabled');
  });

  return true;
}

function updateTopStudio19Rate() {
  const variantId = getCurrentVariantId();
  if (!variantId) return false;

  /** @type {Record<string, Studio19VariantData> | undefined} */
  const allVariantData = /** @type {{ initialVariantData?: Record<string, Studio19VariantData> }} */ (window)
    .initialVariantData;
  if (!allVariantData || typeof allVariantData !== 'object') return false;

  const variantData = allVariantData[String(variantId)];
  const rate = getRateFromVariantData(variantData);
  const formattedRate = rate !== null ? formatRate(rate) : null;

  if (!formattedRate) return false;

  const textNodes = getTopStudio19TextNodes();
  if (!textNodes.length) return false;

  // Ensure the Apply link exists (created as a sibling of the paragraph). Its href/visibility is
  // managed separately by updateTopStudio19ApplyLink; here we only set the rent text.
  if (!getTopStudio19ApplyNodes().length) return false;

  textNodes.forEach((paragraph) => {
    paragraph.textContent = `Rent from ${formattedRate}/week`;
  });

  return true;
}

function syncTopStudio19Rate(retriesLeft = MAX_RETRIES) {
  const didUpdateRate = updateTopStudio19Rate();
  const didUpdateApply = updateTopStudio19ApplyLink();

  if (didUpdateRate && didUpdateApply) return;

  // Rate data has loaded but this variant has no Studio19 rental → definitively unavailable;
  // show the "Unavailable" label now rather than waiting out the retries.
  if (!didUpdateRate && isStudio19RateUnavailable()) {
    setTopStudio19Unavailable();
    return;
  }

  if (retriesLeft > 0) {
    window.setTimeout(() => syncTopStudio19Rate(retriesLeft - 1), RETRY_DELAY_MS);
    return;
  }

  // Retries exhausted without ever resolving a rate (data never loaded) — surface Unavailable
  // instead of leaving a bare "Rent from".
  if (!didUpdateRate) setTopStudio19Unavailable();
}

function isProductTemplate() {
  return document.body?.dataset.template?.includes('product') ?? false;
}

if (isProductTemplate()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncTopStudio19Rate(), { once: true });
  } else {
    syncTopStudio19Rate();
  }

  document.addEventListener(ThemeEvents.variantUpdate, () => {
    syncTopStudio19Rate();
  });
}
